import { ApiClient } from '../ApiClient.js';
import { CacheService } from '../CacheService.js';
import { DealsApiService } from './DealsApiService.js';
import {
    mapClientFromApi,
    mapContactFromApi,
    mapDealFromApi,
    mapScheduleFromApi
} from './ApiEntityMapper.js';
import { restrictDealsToRecordScope } from '../../utils/CrmDealFilters.js';
import { auth } from '../AuthService.js';
import {
    LIST_VIEW_MAX_PAGES,
    LIST_VIEW_PER_PAGE,
    listViewInitialState,
    reduceListViewPage,
} from '../../../deals/listViewPaging.js';

const inflight = new Map();

function dedupe(key, fn) {
    if (inflight.has(key)) return inflight.get(key);
    const promise = fn().finally(() => {
        if (inflight.get(key) === promise) {
            inflight.delete(key);
        }
    });
    inflight.set(key, promise);
    return promise;
}

export async function fetchAllPages(path, params = {}, perPage = 100) {
    const userId = auth.getSession()?.userId || 'anon';
    const cacheKey = `pages:${userId}:${path}:${JSON.stringify(params)}`;
    
    return CacheService.getOrSet(cacheKey, async () => {
        const items = [];
        const pageSize = Math.min(100, Math.max(1, perPage));
        const MAX_PAGES = 20;
        const baseParams = { ...params };
        delete baseParams.page;
        delete baseParams.cursor;
        let page = 1;
        let cursor = params.cursor || null;

        for (let i = 0; i < MAX_PAGES; i++) {
            const query = { ...baseParams, per_page: pageSize };
            if (cursor) query.cursor = cursor;
            else query.page = page;

            const env = await ApiClient.getWithMeta(path, { params: query });
            const list = Array.isArray(env?.data) ? env.data : [];
            if (!list.length) break;
            items.push(...list);

            const nextCursor = env?.meta?.next_cursor;
            if (nextCursor) {
                cursor = nextCursor;
                continue;
            }
            if (list.length < pageSize) break;
            if (cursor) break;
            page += 1;
        }
        return items;
    }, 2 * 60 * 1000); // 2 minute TTL for paginated data
}

export class CrmApiRepository {
    static async getAllClients() {
        return dedupe('clients', async () => {
            const rows = await fetchAllPages('/api/v1/clients');
            return rows.map(mapClientFromApi).filter(Boolean);
        });
    }

    static async getAllClientsLookup() {
        return dedupe('clients:lookup', async () => {
            const rows = await fetchAllPages('/api/v1/clients/lookup');
            return rows.map(mapClientFromApi).filter(Boolean);
        });
    }

    static async getClientById(clientId) {
        const cacheKey = `client:${clientId}`;
        return CacheService.getOrSet(cacheKey, async () => {
            const row = await ApiClient.get(`/api/v1/clients/${encodeURIComponent(clientId)}`);
            return row ? mapClientFromApi(row) : null;
        }, 5 * 60 * 1000);
    }

    static async getAllContacts(clientId = null) {
        const key = clientId ? `contacts:${clientId}` : 'contacts';
        return dedupe(key, async () => {
            const params = clientId ? { client_id: clientId } : {};
            const rows = await fetchAllPages('/api/v1/contacts', params);
            return rows.map(mapContactFromApi).filter(Boolean);
        });
    }

    static async getAllContactsLookup() {
        return dedupe('contacts:lookup', async () => {
            const rows = await fetchAllPages('/api/v1/contacts/lookup');
            return rows.map(mapContactFromApi).filter(Boolean);
        });
    }

    static async getContactById(contactId) {
        const cacheKey = `contact:${contactId}`;
        return CacheService.getOrSet(cacheKey, async () => {
            const row = await ApiClient.get(`/api/v1/contacts/${encodeURIComponent(contactId)}`);
            return row ? mapContactFromApi(row) : null;
        }, 5 * 60 * 1000);
    }

    static async getAllDeals() {
        const userId = auth.getSession()?.userId || 'anon';
        return dedupe(`deals:${userId}`, async () => {
            const cacheKey = `deals:all:${userId}`;
            return CacheService.getOrSet(cacheKey, async () => {
                const maxSafePages = 3;
                const allItems = [];
                
                for (let page = 1; page <= maxSafePages; page++) {
                    const rows = await ApiClient.get('/api/v1/deals', {
                        page,
                        per_page: 100
                    });
                    
                    if (!rows?.length) break;
                    
                    allItems.push(...rows);
                    
                    if (rows.length < 100) break;
                }
                
                return restrictDealsToRecordScope(
                    allItems.map(mapDealFromApi).filter(Boolean)
                );
            }, 1 * 60 * 1000); // 1 minute TTL for deals (more frequent updates)
        });
    }

    /** Lightweight deal→contact/owner links (Contacts enrichment). */
    static async getDealContactLinks() {
        const userId = auth.getSession()?.userId || 'anon';
        const cacheKey = `deals:contact-links:${userId}`;
        return CacheService.getOrSet(cacheKey, async () => {
            const rows = await ApiClient.get('/api/v1/deals/contact-links');
            const list = Array.isArray(rows) ? rows : [];
            return list.map((r) => ({
                id: r.id,
                primaryContactId: r.primary_contact_id || null,
                ownerUserId: r.owner_user_id || null,
            }));
        }, 60 * 1000);
    }

    static async getDealById(dealId) {
        const cacheKey = `deal:${dealId}`;
        return CacheService.getOrSet(cacheKey, async () => {
            const row = await ApiClient.get(`/api/v1/deals/${encodeURIComponent(dealId)}`);
            return row ? mapDealFromApi(row) : null;
        }, 2 * 60 * 1000);
    }

    static async getSchedulesByDeal(dealId) {
        return dedupe(`schedules:${dealId}`, async () => {
            const rows = await fetchAllPages(`/api/v1/deals/${encodeURIComponent(dealId)}/schedules`);
            return rows.map(mapScheduleFromApi).filter(Boolean);
        });
    }

    static async getSchedulesBatch(dealIds) {
        if (!dealIds?.length) return [];
        const validDealIds = dealIds.filter(id => id && typeof id === 'string' && id !== 'undefined');
        if (!validDealIds.length) return [];

        const cacheKey = `schedules:batch:${validDealIds.slice().sort().join(',')}`;
        return CacheService.getOrSet(cacheKey, async () => {
            // Chunk the request so the query string never grows past proxy/server
            // URL length limits (a single all-deals request 414s on production).
            const CHUNK_SIZE = 40;
            const grouped = {};
            for (let i = 0; i < validDealIds.length; i += CHUNK_SIZE) {
                const chunk = validDealIds.slice(i, i + CHUNK_SIZE);
                const part = await DealsApiService.getSchedulesBatch(chunk);
                if (part && typeof part === 'object') {
                    Object.assign(grouped, part);
                }
            }
            return Object.values(grouped).flat().map(mapScheduleFromApi).filter(Boolean);
        }, 2 * 60 * 1000);
    }

    static clearCache() {
        inflight.clear();
        CacheService.invalidatePattern('^pages:');
        CacheService.invalidate('contacts:enriched:all');
        CacheService.invalidate('users:enriched:all');
        CacheService.invalidate('clients:enriched:all');
        CacheService.invalidatePattern('^client:');
        CacheService.invalidatePattern('^deals:all');
        CacheService.invalidatePattern('^deals:list-view');
        CacheService.invalidatePattern('^deals:enriched:all');
        CacheService.invalidatePattern('^deal:');
        CacheService.invalidatePattern('^schedules:');
        CacheService.invalidatePattern('^deals:contact-links');
    }

    static async getAllSchedules() {
        const userId = auth.getSession()?.userId || 'anon';
        return CacheService.getOrSet(`schedules:all:${userId}`, async () => {
            try {
                const rows = await fetchAllPages('/api/v1/schedules', {}, 100);
                return rows.map(mapScheduleFromApi).filter(Boolean);
            } catch (err) {
                // Fallback: previous N/40 batch path if flat list unavailable
                console.warn('[CrmApiRepository] flat /schedules failed; falling back to deal batch', err);
                const deals = await CrmApiRepository.getAllDeals();
                const dealIds = deals
                    .map(d => d.id)
                    .filter(id => id && typeof id === 'string' && id !== 'undefined');
                if (!dealIds.length) return [];
                return CrmApiRepository.getSchedulesBatch(dealIds);
            }
        }, 2 * 60 * 1000);
    }

    static async getSchedulesByDateRange(startDate, endDate) {
        const cacheKey = `schedules:range:${startDate}:${endDate}`;
        return CacheService.getOrSet(cacheKey, async () => {
            try {
                const params = {};
                if (startDate) params.due_from = String(startDate).slice(0, 10);
                if (endDate) params.due_to = String(endDate).slice(0, 10);
                const rows = await fetchAllPages('/api/v1/schedules', params, 100);
                return rows.map(mapScheduleFromApi).filter(Boolean);
            } catch (err) {
                console.warn('[CrmApiRepository] ranged /schedules failed; filtering full list', err);
                const schedules = await CrmApiRepository.getAllSchedules();
                return schedules.filter(s => {
                    const d = s.invoiceDate || s.dueDate;
                    return d && d >= startDate && d <= endDate;
                });
            }
        }, 2 * 60 * 1000);
    }

    /**
     * Capped list-view walk for Table/Kanban (server display names, no child graph).
     * Returns { rows, total, truncated }. Caps at LIST_VIEW_MAX_PAGES × perPage.
     */
    static async getDealsListViewCapped({
        maxPages = LIST_VIEW_MAX_PAGES,
        perPage = LIST_VIEW_PER_PAGE,
    } = {}) {
        const userId = auth.getSession()?.userId || 'anon';
        const pageSize = Math.min(100, Math.max(1, perPage));
        return dedupe(`deals:list-view:${userId}`, async () => {
            const cacheKey = `deals:list-view:${userId}`;
            return CacheService.getOrSet(cacheKey, async () => {
                let state = listViewInitialState();
                for (let i = 0; i < maxPages; i++) {
                    const env = await DealsApiService.listView(
                        state.cursor
                            ? { cursor: state.cursor, per_page: pageSize }
                            : { page: state.page, per_page: pageSize }
                    );
                    state = reduceListViewPage(state, env, pageSize);
                    if (state.done) break;
                    if (i === maxPages - 1) break;
                }
                const rows = restrictDealsToRecordScope(
                    state.items.map(mapDealFromApi).filter(Boolean)
                );
                const total = state.total != null ? state.total : rows.length;
                const truncated = state.total != null
                    ? state.items.length < state.total
                    : !state.done;
                return { rows, total, truncated };
            }, 60 * 1000);
        });
    }

    /** Lightweight deals for Table/Kanban (server display names). */
    static async getAllDealsListView() {
        const result = await CrmApiRepository.getDealsListViewCapped();
        return result.rows;
    }

    /** Delta catch-up: deals updated after `sinceIso` (bypass list cache). */
    static async getDealsListViewUpdatedSince(sinceIso) {
        if (!sinceIso) return [];
        let state = listViewInitialState();
        const pageSize = LIST_VIEW_PER_PAGE;
        for (let i = 0; i < LIST_VIEW_MAX_PAGES; i++) {
            const env = await DealsApiService.listView(
                state.cursor
                    ? { cursor: state.cursor, per_page: pageSize, updated_since: sinceIso }
                    : { page: state.page, per_page: pageSize, updated_since: sinceIso }
            );
            state = reduceListViewPage(state, env, pageSize);
            if (state.done) break;
        }
        return restrictDealsToRecordScope(state.items.map(mapDealFromApi).filter(Boolean));
    }
}

if (typeof window !== 'undefined') {
    ['dealSaved', 'dealDeleted', 'clientSaved', 'clientDeleted', 'contactSaved', 'contactDeleted', 'userSaved', 'userDeleted', 'dataImported', 'dealActivitiesChanged'].forEach(evt => {
        window.addEventListener(evt, () => CrmApiRepository.clearCache());
    });
}
